// The chat with RK Intelligence (REQ-AGT-01, 05, 09): a tool-use loop over the catalogue of tools.
// Read tools see only what the asking user may see; proposal tools only create proposals.
import { CHAT_PROMPT, SYSTEM_PROMPT } from "./prompts.generated.ts";
import { farmStaffOnly, isStatementTimeout, logQuietly, READ_TOOLS, redacted, runTool, toolsFor, viewOnly, type ToolContext } from "./tools.ts";
import { usageOf, type MessagesClient, type ModelMessage } from "./model.ts";
import type { Usage } from "./pipeline.ts";

export type ChatTurn = { role: "user" | "assistant"; content: unknown };
export type ChatChart = { title: string; spec: Record<string, unknown>; rows: Record<string, unknown>[] };
export type ChatResult = {
  reply: string; usage: Usage[]; tool_calls: { name: string; input: unknown; ok: boolean; refused?: boolean }[]; proposals: { proposal_id: string; ref: string }[]; charts: ChatChart[];
  stop: "answered" | "refused" | "too_long" | "too_many_steps"; turns: ChatTurn[];
  /** Time per step (speed analysis, owner 26 Oct 2026): each model call and the tool calls it asked for, in ms.
   *  Decision 124 #8: ttft_ms, from sending a streamed call to the first event of its answer, and first_text_ms, to its
   *  first visible words (each null when not streamed, or, for first_text_ms, when the call wrote no words); speed, the
   *  speed the API served the call at (usage.speed as returned, null when the reply carries none). */
  steps?: { model_ms: number; ttft_ms?: number | null; first_text_ms?: number | null; tools_ms: number; tools: number; speed?: string | null; cache_read?: number; cache_written?: number; input?: number }[];
  /** Which model answered (speed, charter point 7): the fast one for a simple lookup, else the strong one. */
  route?: "fast" | "strong";
  /** Decision 93 fix 1: how long the standing-figures read (snapshotOf) before the first model call took, in ms. Set
   *  by the live wiring (supabase/functions/agent/index.ts), which reads the figures before it calls runChat. */
  snapshot_ms?: number;
};

// Words that make a message more than a simple lookup: a change to prepare, or work in several steps (English and
// Afrikaans). Anything that matches goes to the strong model; so does anything after the first turn, where
// "it", "that" and "those" must be resolved from the conversation.
const ACTION_WORDS = /\b(record|propose|approve|pay (it|them|this|that|the|now|in)|send|e-?mail|draft|create|add|close|cancel|move|mark|tag(ged)?|caught|catch|counted|count \d|put .* down|split|void|reinstate|write (it )?off|transfer|remind|task|query|deliver(ed)? (today|yesterday)|correct|change|fix|betaal|skuif|skep|stuur|merk|gevang|getel|keur .* goed|maak .* reg)\b/i;
const QUESTION = /^(how|what|which|when|where|who|is|are|do|does|did|have|has|show|list|give|tell|hoeveel|wat|watter|wanneer|waar|wie|is daar|het ons|wys)\b/i;

/** A simple lookup the fast model may answer: one short question, first in the conversation, asking for no change. */
export function isSimpleLookup(message: string, history: ChatTurn[]): boolean {
  const m = message.trim();
  return history.length === 0 && m.length <= 160 && QUESTION.test(m) && !ACTION_WORDS.test(m) && (m.match(/[.?!]/g)?.length ?? 0) <= 1;
}

// Decision 124 #8 (effort): an ALLOWLIST (round 3, B2). Effort "low" only for a greeting alone, or a narrow lookup of
// exactly one record, lot or species in one of a few fixed question frames (round 1's simple cases). Anything else,
// however short (money safety, approvals, judgement, summaries, several records, a greeting with a request), keeps the
// model's default effort. Words are matched case-insensitively, record numbers too (s26-122 is S26-122).
const GREETING_ALONE = /^(hi|hello|hey|hallo|haai|howzit|good (morning|afternoon|evening|day)|goeie ?(m[oô]re|middag|naand|dag)|m[oô]re|dag)( there)?[\s!.,]*$/iu;
/** One record's number: S26-122, PR-0010, DEL-0001, WWV0016487. */
const ONE_REF = String.raw`[a-z]{1,5}\d*-?\d{2,}`;
/** One species, as the register names them, singular or plural, with at most one of these qualifiers. */
const ONE_SPECIES = String.raw`(?:(?:black|blue|golden|common|red|white|cape|king|mountain|plains) )?`
  + String.raw`(?:springbok|kudu|eland|impala|gemsbok|oryx|sable|roan|wildebeest|waterbuck|lechwe|blesbok|nyala|hartebeest|giraffe|steenbok|duiker|`
  + String.raw`buffalo|zebra|bontebok|tsessebe|bushbuck|reedbuck|klipspringer|ostrich|warthog)(?:s|es|ke)?`;
const END = String.raw`\s*\??$`;
const LOOKUP_FRAMES: readonly RegExp[] = [
  new RegExp(`^what is the balance (?:on|of) ${ONE_REF}${END}`, "i"),
  new RegExp(`^how many ${ONE_SPECIES} do we have${END}`, "i"),
  new RegExp(`^which (?:queries|tasks) are open${END}`, "i"),
  // The owner's question 3 (Decision 124 #15b): one lot, named once more by its species and seller.
  new RegExp(String.raw`^has lot \d{1,4}(?:, the ${ONE_SPECIES} from [\p{L}' ]{1,60},)? been invoiced${END}`, "iu")
];

/**
 * Decision 124 #8: a turn answered at effort "low": the first message of a conversation, and only a greeting alone or
 * a narrow single-record lookup in one of LOOKUP_FRAMES. Every other message, and any later turn, keeps the model's
 * default effort.
 */
export function isSimpleTurn(message: string, history: ChatTurn[]): boolean {
  if (history.length) return false;
  const m = message.trim().replace(/\s+/g, " ");
  return GREETING_ALONE.test(m) || LOOKUP_FRAMES.some((f) => f.test(m));
}

/** Decision 124 #8: the models with fast mode (speed "fast") on the plain Messages API; any other model never gets it. */
export const FAST_MODE_MODELS: ReadonlySet<string> = new Set(["claude-opus-5-5"]);

// Decision 124 #8 (round 2, F4): when the API refuses speed itself (a 400 invalid_request_error naming speed or fast
// mode), this function instance sends no speed "fast" for 10 minutes, then tries it again. Kept in this module, so per
// function instance; the clock is Date.now().
export const FAST_MODE_HOLD_MS = 10 * 60_000;
let fastModeHeldUntil = 0;
/**
 * An error the SDK would have retried (round 3, B1): its status ("408", "409", "429", "5xx" as the number), or
 * "connection" for a connection error or a timeout; null for anything else (an abort is never retried).
 */
function sdkWouldRetry(e: unknown): string | null {
  const status = (e as { status?: unknown } | null)?.status;
  if (typeof status === "number") return status === 408 || status === 409 || status === 429 || status >= 500 ? String(status) : null;
  for (let c = (e as object | null) ? Object.getPrototypeOf(e) : null; c; c = Object.getPrototypeOf(c)) {
    const name = (c as { constructor?: { name?: string } }).constructor?.name;
    if (name === "APIUserAbortError") return null;
    if (name === "APIConnectionError") return "connection";
  }
  return null;
}
/** The API refused the speed field (not a general bad request, which is never retried). */
function speedRefused(e: unknown): { message: string } | null {
  const err = e as { status?: unknown; message?: unknown; error?: { type?: unknown; message?: unknown; error?: { type?: unknown; message?: unknown } } } | null;
  if (err?.status !== 400) return null;
  const body = err.error?.error ?? err.error;
  if (body?.type !== "invalid_request_error") return null;
  const message = String(body.message ?? err.message ?? "");
  return /speed|fast[ -]?mode/i.test(message) ? { message: message.slice(0, 300) } : null;
}

const MAX_STEPS = 10;

export function chatContext(user: { name: string; roles: string[] }, today: string, snapshot?: string | null): string {
  return `\n\nContext\n- You are talking with ${user.name} (${user.roles.join(", ") || "no role"}). Today is ${today} (South African time).\n`
    + "- Money in tool results is in cents unless a formatted rand amount is given; always answer in rand, e.g. R1,656,000.00.\n"
    + "- If a tool returns no rows, say plainly that there are no records; never estimate.\n"
    + "- After a propose tool call, tell the user the card's number and that the card waits for their approval below."
    + (viewOnly(user.roles) ? "\n- This person is view only: they can read everything but cannot propose any change, and you have no propose tools for them. Never offer to prepare, propose or set up anything; say plainly that they cannot, and name who can (the CEO or the GM)." : "")
    + (farmStaffOnly(user.roles)
      ? "\n- This person is farm staff: they report their field work (counts, sightings, missing and found animals, deaths, calves and moves, for the GM to approve; arrivals and queries, which go in at once, an arrival awaiting the GM's review), and update the tasks given to them. You have tools for nothing else. When they need something they cannot do themselves (a new task, a payment, an invoice approval, a document link, a change to a person or a login), offer to raise a query to the GM." : "")
    + (snapshot ? `\n\nFigures read from the records just now, as the asking user may see them (charter point 7):\n${snapshot}\n`
      + "- When the question is answered by one of these figures, answer at once from it, in one line, without a tool call; then add detail only if it helps. For anything more specific, use the tools."
      // Owner's decision 37 (BD-160): a filtered figure is always looked up, never worked out from these totals.
      + "\n- These are totals for everything the asking user may see. Anything filtered (one supplier, one invoice, one camp, one species, one date range) must be looked up with the tools, never derived from these totals." : "");
}

/** What the user sees while an answer is being prepared: what is being looked up, then the answer's words as they come. */
export type ChatEvent = { type: "status"; text: string } | { type: "text"; text: string };

/** Plain words for what each tool is doing, shown while it runs. */
const TOOL_STATUS: Record<string, string> = {
  search_invoices: "Looking up invoices", get_invoice: "Opening the invoice", list_items: "Looking at the animals on order", ready_to_pay: "Working out what is ready to pay", upfront_payments: "Checking what we paid before delivery",
  waiting_report: "Checking what is still to come", herd_summary: "Counting the animals", propose_catch_from_herd: "Preparing the catch from the herd for approval", propose_record_sex: "Preparing the sex record for approval", propose_payment_approval: "Checking what is ready to pay", propose_arrival_review: "Preparing the arrival review for approval", find_animal: "Finding the animal", get_register: "Reading the register", place_counts: "Checking the camp",
  list_queries: "Looking at the queries", proposal_checks: "Checking the card", who_may_decide: "Checking who may decide", get_document: "Opening the document", search_documents: "Looking in the inbox", account_for_documents: "Checking every file that arrived and what became of it", list_proposals: "Looking at what is waiting", run_report: "Running the report"
};

/**
 * Owner's decision 81 #5 (29 Oct 2026, the 04:34–04:35 UTC timeouts): every read tool call has a time limit. After it
 * the call ends as a statement timeout would, even if the database never answers (the statement timeout is 8 s).
 */
export const TOOL_TIME_LIMIT_MS = 6000;
const READ_TOOL_NAMES = new Set(READ_TOOLS.map((t) => t.name));

/** Plain words for what each read tool reads, for a read that timed out (decision 81 #5). */
const TOOL_READS: Record<string, string> = {
  search_invoices: "the invoices", get_invoice: "the invoice", list_items: "the animals on order", ready_to_pay: "what is ready to pay", upfront_payments: "the payments made before delivery",
  waiting_report: "what is still to come", herd_summary: "the animal totals", find_animal: "the animal", get_register: "the register", get_tag_link: "the tag link", place_counts: "the place",
  list_queries: "the queries", proposal_checks: "the card's checks", list_proposals: "the cards", who_may_decide: "who may decide", get_document: "the document",
  search_documents: "the inbox", run_report: "the report", list_findings: "the findings", account_for_documents: "the files that arrived and what became of them",
  company_details: "the company's legal details"
};
/** What the model is told when a read timed out: plain words, never the database's own message. */
export const toolTimeoutMessage = (tool: string): string =>
  `Could not read ${TOOL_READS[tool] ?? `the ${tool.replace(/_/g, " ")}`} just now (the database took too long). Try again or ask for less.`;

/** The read ended at its time limit (a statement timeout as far as anyone reading the log is concerned). */
class ToolTimeLimit extends Error {
  code = "57014";
  constructor() { super(`The read did not answer within ${TOOL_TIME_LIMIT_MS} ms (RK Intelligence's time limit for a read tool).`); }
}

/** Runs a read tool within TOOL_TIME_LIMIT_MS; the timer is cleared as soon as the read answers. */
async function withinTimeLimit<T>(run: () => Promise<T>): Promise<T> {
  let timer: ReturnType<typeof setTimeout> | undefined;
  try {
    return await Promise.race([run(), new Promise<never>((_, reject) => { timer = setTimeout(() => reject(new ToolTimeLimit()), TOOL_TIME_LIMIT_MS); })]);
  } finally {
    clearTimeout(timer);
  }
}

export async function runChat(o: {
  client: MessagesClient; model: string; history: ChatTurn[]; message: string; ctx: ToolContext; user: { name: string; roles: string[] }; today: string;
  /** A faster model for simple lookups (isSimpleLookup); none means the strong model answers everything. */
  fastModel?: string | null;
  /** Figures read before the first call (snapshotOf), so a common question needs no tool round trip. */
  snapshot?: string | null;
  /** Called as the answer is prepared, for showing progress (streaming). */
  onEvent?: (e: ChatEvent) => void;
  /** Decision 124 #8: fast mode (speed "fast") on the chat calls, from the setting chat_fast_mode; only on a model with it. */
  fastMode?: boolean;
}): Promise<ChatResult> {
  const turns: ChatTurn[] = [...o.history, { role: "user", content: o.message }];
  const usage: Usage[] = [];
  const toolCalls: ChatResult["tool_calls"] = [];
  const proposals: ChatResult["proposals"] = [];
  const charts: ChatChart[] = [];
  const route: "fast" | "strong" = o.fastModel && isSimpleLookup(o.message, o.history) ? "fast" : "strong";
  const model = route === "fast" ? o.fastModel! : o.model;
  const steps: NonNullable<ChatResult["steps"]> = [];
  // Decision 124 #8: one effort and one speed for every call of the turn (a change between calls invalidates the
  // prompt cache). Effort "low" only for a simple first message; otherwise none is sent (the model's default).
  // Thinking is left as it was (not sent): it is never disabled.
  const effort = isSimpleTurn(o.message, o.history) ? { output_config: { effort: "low" } } : {};
  let fast = Boolean(o.fastMode) && FAST_MODE_MODELS.has(model) && Date.now() >= fastModeHeldUntil;
  const tools = toolsFor(o.user.roles);
  const offered = new Set(tools.map((t) => t.name));
  const byName = new Map(tools.map((t) => [t.name, t]));
  for (let step = 0; step < MAX_STEPS; step++) {
    const t0 = Date.now();
    // Prompt caching: the tools and the system prompt are the same for every call, and each step of the loop
    // resends the conversation so far; both are read from the cache (faster first words, a tenth of the cost).
    const params = {
      model,
      max_tokens: 8000,
      system: [{ type: "text", text: `${SYSTEM_PROMPT}\n${CHAT_PROMPT}`, cache_control: { type: "ephemeral" } }, { type: "text", text: chatContext(o.user, o.today, o.snapshot) }],
      tools,
      messages: withCacheBreakpoint(turns),
      ...effort
    };
    let ttft: number | null = null;
    let firstText: number | null = null;
    // Decision 124 #8 (round 2, F3): the fast call is sent with the SDK's retries off ({ maxRetries: 0 }), so a fast-mode
    // 429 goes straight to the standard resend below instead of waiting out retry-after at fast speed; the standard
    // call keeps the SDK's normal retries.
    const call = async (p: Record<string, unknown>, opts?: { maxRetries: number }): Promise<ModelMessage> => {
      ttft = null;
      firstText = null;
      if (!o.onEvent) return opts ? o.client.messages.create(p, opts) : o.client.messages.create(p);
      // Streamed, so the first words show while the rest is written. The first event of the answer (not the
      // connection opening) times the wait for the first token, and the first words time the first visible text.
      const stream = opts ? o.client.messages.stream(p, opts) : o.client.messages.stream(p);
      stream.on?.("streamEvent", () => { ttft ??= Date.now() - t0; });
      stream.on?.("text", (delta) => {
        if (delta) firstText ??= Date.now() - t0;
        o.onEvent!({ type: "text", text: delta });
      });
      return stream.finalMessage();
    };
    let m: ModelMessage;
    try {
      m = await call(fast ? { ...params, speed: "fast" } : params, fast ? { maxRetries: 0 } : undefined);
    } catch (e) {
      // Decision 124 #8: the fast call goes out with the SDK's retries off, so it is resent here, once, at standard
      // speed (where the SDK's own retries apply) for every error the SDK would have retried (round 3, B1): 408, 409,
      // 429 (fast mode's own rate limit), 5xx and 529, a connection error or a timeout; and for a 400 that refuses
      // speed itself (round 2, F4), after which fast mode is held for 10 minutes in this function instance. Only while
      // no stream event has arrived: once the answer has started, words may be on the screen, so it is never sent
      // again. The rest of the turn stays at standard speed, and the fallback is logged. Any other error is not
      // retried, and an error on the resend is not retried again here.
      const refused = fast ? speedRefused(e) : null;
      const transient = fast ? sdkWouldRetry(e) : null;
      if (!fast || ttft !== null || (!refused && !transient)) throw e;
      fast = false;
      if (refused) {
        fastModeHeldUntil = Date.now() + FAST_MODE_HOLD_MS;
        await logQuietly(o.ctx.logError, "Fast mode was refused by the model service (400 invalid_request_error): the chat call was sent again at standard speed, and fast mode is held for 10 minutes.",
          { step: "chat", status: 400, model, conversation_id: o.ctx.conversationId, elapsed_ms: Date.now() - t0, api_message: refused.message, held_ms: FAST_MODE_HOLD_MS });
      } else {
        await logQuietly(o.ctx.logError, transient === "429"
          ? "Fast mode was rate limited (429): the chat call was sent again at standard speed."
          : `The fast-mode chat call failed (${transient}) before its answer started: it was sent again at standard speed.`,
          { step: "chat", status: transient, model, conversation_id: o.ctx.conversationId, elapsed_ms: Date.now() - t0 });
      }
      m = await call(params);
    }
    usage.push(usageOf(model, m));
    // Cache use per call (decision 21): a missed cache shows as cache_read 0 with the guidance written again.
    steps.push({ model_ms: Date.now() - t0, ttft_ms: ttft, first_text_ms: firstText, tools_ms: 0, tools: 0, speed: typeof m.usage.speed === "string" ? m.usage.speed : null,
      cache_read: m.usage.cache_read_input_tokens ?? 0, cache_written: m.usage.cache_creation_input_tokens ?? 0, input: m.usage.input_tokens });
    const text = m.content.filter((b): b is { type: "text"; text: string } => b.type === "text").map((b) => b.text).join("\n").trim();
    if (m.stop_reason === "refusal") return { reply: "I can't help with that one. Please ask the CEO if it is needed.", usage, tool_calls: toolCalls, proposals, charts, stop: "refused", turns, route, steps };
    turns.push({ role: "assistant", content: m.content });
    if (m.stop_reason === "pause_turn") continue;
    if (m.stop_reason !== "tool_use") {
      return { reply: text || "I have nothing to add.", usage, tool_calls: toolCalls, proposals, charts, stop: m.stop_reason === "max_tokens" ? "too_long" : "answered", turns, route, steps };
    }
    // Every tool call of a step runs at once (charter point 7); results keep the order of the calls.
    const calls = m.content.filter((b) => b.type === "tool_use") as { id: string; name: string; input: Record<string, unknown> }[];
    for (const call of calls) o.onEvent?.({ type: "status", text: `${TOOL_STATUS[call.name] ?? (call.name.startsWith("propose_") ? "Preparing a card for your approval" : "Looking at your records")}…` });
    const toolsStarted = Date.now();
    const outs = await Promise.all(calls.map(async (call) => {
      // A tool not offered in this session (a proposal tool in a Viewer's) is never run (BD-134).
      if (!offered.has(call.name)) return { call, error: "This tool is not available to this person: they can read but not propose changes." };
      // A farm staff session's tools offer only the farm staff list (decision 25): an action outside it is never proposed.
      const choice = (byName.get(call.name)?.input_schema as { properties?: Record<string, { enum?: string[] }> } | undefined)?.properties?.action?.enum;
      const asked = (call.input as Record<string, unknown> | undefined)?.action;
      if (choice && asked != null && !choice.includes(String(asked))) return { call, error: `${String(asked)} is not available to this person; offer to raise a query to the GM instead.` };
      const started = Date.now();
      try {
        const run = () => runTool(call.name, call.input ?? {}, o.ctx);
        return { call, out: READ_TOOL_NAMES.has(call.name) ? await withinTimeLimit(run) : await run() };
      } catch (e) {
        const message = e instanceof Error ? e.message : String(e);
        if (!isStatementTimeout(e)) return { call, error: message };
        // Decision 81 #5: a read that timed out is logged once, and the model is told in plain words.
        await logQuietly(o.ctx.logError, message,
          { tool: call.name, input: redacted(call.input ?? {}), conversation_id: o.ctx.conversationId, elapsed_ms: Date.now() - started, code: "57014" });
        return { call, error: toolTimeoutMessage(call.name) };
      }
    }));
    steps[steps.length - 1] = { ...steps[steps.length - 1]!, tools_ms: Date.now() - toolsStarted, tools: calls.length };
    const results = [];
    for (const r of outs) {
      const { call } = r;
      if ("out" in r && r.out) {
        const out = r.out;
        if (out.proposed) proposals.push({ proposal_id: String(out.proposal_id), ref: String(out.proposal) });
        if (out.chart && Array.isArray(out.rows)) {
          const spec = out.chart as Record<string, unknown>;
          charts.push({ title: String(spec.title ?? "Chart"), spec, rows: out.rows as Record<string, unknown>[] });
        }
        toolCalls.push({ name: call.name, input: call.input, ok: true, ...((out as { refused?: boolean })?.refused ? { refused: true } : {}) });
        results.push({ type: "tool_result", tool_use_id: call.id, content: JSON.stringify(out) });
      } else {
        toolCalls.push({ name: call.name, input: call.input, ok: false });
        results.push({ type: "tool_result", tool_use_id: call.id, is_error: true, content: "error" in r ? r.error : "The tool returned nothing." });
      }
    }
    turns.push({ role: "user", content: results });
  }
  return { reply: "That took too many steps. Please ask in a simpler way.", usage, tool_calls: toolCalls, proposals, charts, stop: "too_many_steps", turns, route, steps };
}

/** The conversation with a cache breakpoint on its last block, so the next step reads everything before it from the cache. */
function withCacheBreakpoint(turns: ChatTurn[]): ChatTurn[] {
  if (!turns.length) return turns;
  const last = turns[turns.length - 1]!;
  const blocks = typeof last.content === "string" ? [{ type: "text", text: last.content }] : Array.isArray(last.content) ? [...(last.content as Record<string, unknown>[])] : null;
  if (!blocks?.length) return turns;
  blocks[blocks.length - 1] = { ...blocks[blocks.length - 1], cache_control: { type: "ephemeral" } };
  return [...turns.slice(0, -1), { role: last.role, content: blocks }];
}
